import type {
  TestSetAutoAddRuleFields,
  UploadedConversationMetadataFilterInput,
  UploadedConversationMetricValueFilterInput,
} from "@/types_api/backend/transport/save_test_set_auto_add_rule_request"

// Mirrors the backend validators in domain/test_set_auto_add/schemas.py.
export const AUTO_ADD_MAX_FILTERS = 50
const AUTO_ADD_MAX_AGENT_IDS = 100
export const AUTO_ADD_MAX_PROMPT_GUIDANCE_LENGTH = 2000
export const AUTO_ADD_BUDGET_RANGE = { min: 1, max: 10_000 } as const
export const AUTO_ADD_PENDING_CAP_RANGE = { min: 1, max: 1_000 } as const
export const AUTO_ADD_METRIC_OPERATORS = [
  ">",
  "<",
  "=",
  ">=",
  "<=",
  "!=",
  "range",
  "exists",
] as const satisfies readonly UploadedConversationMetricValueFilterInput["operator"][]
export const AUTO_ADD_STRING_METRIC_OPERATORS = ["=", "!=", "exists"] as const

export type AutoAddMetadataFilter = UploadedConversationMetadataFilterInput &
  Required<Pick<UploadedConversationMetadataFilterInput, "match_type">>
export type AutoAddMetricValueFilter =
  UploadedConversationMetricValueFilterInput

export interface AutoAddRuleFormState {
  enabled: boolean
  metadataFilters: AutoAddMetadataFilter[]
  metricValueFilters: AutoAddMetricValueFilter[]
  agentIds: string[]
  /** Percent, 0 < x <= 100, as typed. */
  sampleRatePercent: string
  originalSampleRate?: number
  promptGuidance: string
  evaluationBudgetPerDay: string
  maxPendingTestCases: string
}

export const DEFAULT_AUTO_ADD_RULE_FORM: AutoAddRuleFormState = {
  enabled: true,
  metadataFilters: [],
  metricValueFilters: [],
  agentIds: [],
  sampleRatePercent: "100",
  promptGuidance: "",
  evaluationBudgetPerDay: "50",
  maxPendingTestCases: "20",
}

export function autoAddRuleFormFromFields(
  fields: TestSetAutoAddRuleFields | null | undefined,
): AutoAddRuleFormState {
  if (!fields) {
    return DEFAULT_AUTO_ADD_RULE_FORM
  }
  return {
    enabled: fields.enabled ?? true,
    metadataFilters: (fields.metadata_filters ?? []).map((filter) => ({
      ...filter,
      key: filter.key,
      value: filter.value,
      match_type: filter.match_type ?? "EQUALS",
    })),
    metricValueFilters: (fields.metric_value_filters ?? []).map((filter) => ({
      ...filter,
    })),
    agentIds: [...(fields.agent_ids ?? [])],
    sampleRatePercent: formatPercent(fields.sample_rate ?? 1),
    originalSampleRate: fields.sample_rate ?? 1,
    promptGuidance: fields.prompt_guidance ?? "",
    evaluationBudgetPerDay: String(fields.evaluation_budget_per_day ?? 50),
    maxPendingTestCases: String(fields.max_pending_test_cases ?? 20),
  }
}

function formatPercent(sampleRate: number): string {
  const [coefficient, exponent = "0"] = String(sampleRate).split("e")
  return String(Number(`${coefficient}e${Number(exponent) + 2}`))
}

function parseInteger(value: string): number | null {
  const trimmed = value.trim()
  if (!/^-?\d+$/.test(trimmed)) {
    return null
  }
  return Number(trimmed)
}

function isFiniteNumberString(value: string): boolean {
  const trimmed = value.trim()
  return trimmed !== "" && Number.isFinite(Number(trimmed))
}

type AutoAddRuleFormErrors = Partial<
  Record<
    | "sampleRatePercent"
    | "promptGuidance"
    | "evaluationBudgetPerDay"
    | "maxPendingTestCases"
    | "metadataFilters"
    | "metricValueFilters"
    | "agentIds",
    string
  >
>

export function validateAutoAddRuleForm(
  form: AutoAddRuleFormState,
): AutoAddRuleFormErrors {
  const errors: AutoAddRuleFormErrors = {}
  const percent = Number(form.sampleRatePercent.trim())
  if (
    form.sampleRatePercent.trim() === "" ||
    !Number.isFinite(percent) ||
    percent <= 0 ||
    percent > 100
  ) {
    errors.sampleRatePercent = "Sample rate must be above 0% and at most 100%."
  }
  if (form.promptGuidance.trim().length > AUTO_ADD_MAX_PROMPT_GUIDANCE_LENGTH) {
    errors.promptGuidance = `Guidance must be at most ${AUTO_ADD_MAX_PROMPT_GUIDANCE_LENGTH} characters.`
  }
  const budget = parseInteger(form.evaluationBudgetPerDay)
  if (
    budget === null ||
    budget < AUTO_ADD_BUDGET_RANGE.min ||
    budget > AUTO_ADD_BUDGET_RANGE.max
  ) {
    errors.evaluationBudgetPerDay = `Budget must be a whole number from ${AUTO_ADD_BUDGET_RANGE.min} to ${AUTO_ADD_BUDGET_RANGE.max}.`
  }
  const cap = parseInteger(form.maxPendingTestCases)
  if (
    cap === null ||
    cap < AUTO_ADD_PENDING_CAP_RANGE.min ||
    cap > AUTO_ADD_PENDING_CAP_RANGE.max
  ) {
    errors.maxPendingTestCases = `Pending cap must be a whole number from ${AUTO_ADD_PENDING_CAP_RANGE.min} to ${AUTO_ADD_PENDING_CAP_RANGE.max}.`
  }
  if (form.metadataFilters.length > AUTO_ADD_MAX_FILTERS) {
    errors.metadataFilters = `At most ${AUTO_ADD_MAX_FILTERS} metadata filters.`
  } else if (form.metadataFilters.some((filter) => filter.key.trim() === "")) {
    errors.metadataFilters = "Every metadata filter needs a key."
  } else if (
    form.metadataFilters.some(
      (filter) =>
        filter.match_type === "ANY_OF" &&
        (!filter.values?.length || filter.values.length > AUTO_ADD_MAX_FILTERS),
    )
  ) {
    errors.metadataFilters = `Each any-of filter needs 1 to ${AUTO_ADD_MAX_FILTERS} alternatives.`
  }
  if (form.metricValueFilters.length > AUTO_ADD_MAX_FILTERS) {
    errors.metricValueFilters = `At most ${AUTO_ADD_MAX_FILTERS} metric filters.`
  } else {
    const invalid = form.metricValueFilters.find(
      (filter) => metricValueFilterError(filter) !== null,
    )
    if (invalid) {
      errors.metricValueFilters = metricValueFilterError(invalid) ?? undefined
    }
  }
  if (form.agentIds.length > AUTO_ADD_MAX_AGENT_IDS) {
    errors.agentIds = `At most ${AUTO_ADD_MAX_AGENT_IDS} agents.`
  }
  return errors
}

/** Same rules as the backend's metric value filter validator. */
function metricValueFilterError(
  filter: AutoAddMetricValueFilter,
): string | null {
  if (filter.metric_id.trim() === "") {
    return "Every metric filter needs a metric."
  }
  if (filter.operator === "range" && !filter.range_max) {
    return "A range filter needs an upper bound."
  }
  if (
    filter.value_type === "string" &&
    !(AUTO_ADD_STRING_METRIC_OPERATORS as readonly string[]).includes(
      filter.operator,
    )
  ) {
    return "Text metrics support =, != and exists."
  }
  if (filter.value_type === "float" && filter.operator !== "exists") {
    if (!isFiniteNumberString(filter.value)) {
      return "Numeric metric filters need a number."
    }
    if (filter.range_max && !isFiniteNumberString(filter.range_max)) {
      return "The range upper bound must be a number."
    }
  }
  return null
}

export function autoAddRuleFieldsFromForm(
  form: AutoAddRuleFormState,
): TestSetAutoAddRuleFields {
  const guidance = form.promptGuidance.trim()
  return {
    enabled: form.enabled,
    metadata_filters: form.metadataFilters.map((filter) => ({
      ...filter,
      key: filter.key.trim(),
      value: filter.value,
      match_type: filter.match_type,
      ...(filter.match_type !== "ANY_OF" && filter.values?.length
        ? { values: [] }
        : {}),
    })),
    metric_value_filters: form.metricValueFilters.map((filter) => ({
      metric_id: filter.metric_id,
      operator: filter.operator,
      value: filter.operator === "exists" ? "" : filter.value.trim(),
      value_type: filter.value_type,
      range_max:
        filter.operator === "range" && filter.range_max
          ? filter.range_max.trim()
          : null,
    })),
    agent_ids: form.agentIds.filter((agentId) => agentId.trim() !== ""),
    sample_rate:
      form.originalSampleRate !== undefined &&
      form.sampleRatePercent === formatPercent(form.originalSampleRate)
        ? form.originalSampleRate
        : Number(form.sampleRatePercent.trim()) / 100,
    prompt_guidance: guidance === "" ? null : guidance,
    evaluation_budget_per_day: Number(form.evaluationBudgetPerDay.trim()),
    max_pending_test_cases: Number(form.maxPendingTestCases.trim()),
  }
}
